import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Settings, X, Clock, MessageSquare, ChevronRight, ArrowRight, Plus, Trash2, Pencil, UserCog, User as UserIcon, Filter, Calendar } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const api = {
  get: async (path) => { const r = await fetch(`${API}/api${path}`, { headers: { Authorization: `Bearer ${localStorage.getItem("qf_token")}` } }); return { data: await r.json(), ok: r.ok }; },
  put: async (path, body) => { const r = await fetch(`${API}/api${path}`, { method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("qf_token")}` }, body: JSON.stringify(body) }); return { data: await r.json(), ok: r.ok }; },
};

const ROLE_LABEL = { owner: "Dueño", administrador: "Administrador", supervisor: "Supervisor", usuario: "Usuario" };

function fmtCurrency(val, cur) {
  if (cur === "CLP") return `$${Math.round(val).toLocaleString("es-CL")}`;
  if (cur === "UF") return `${val.toFixed(2).replace(".", ",")} UF`;
  return `${cur} ${val.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
}

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const days = Math.floor(diff / 86400000);
  if (days === 0) return "Hoy";
  if (days === 1) return "Ayer";
  if (days < 7) return `Hace ${days} días`;
  if (days < 30) return `Hace ${Math.floor(days / 7)} sem.`;
  return new Date(dateStr).toLocaleDateString("es-CL", { day: "numeric", month: "short" });
}

export default function PipelinePage() {
  const { user } = useAuth();
  const [stages, setStages] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [showObsModal, setShowObsModal] = useState(null);
  const [observation, setObservation] = useState("");
  const [selectedQ, setSelectedQ] = useState(null);
  const [history, setHistory] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [editStages, setEditStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reassign, setReassign] = useState(null);
  const [roleFilter, setRoleFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("all");
  // Filtro de fechas: aplica a etapas is_final. Persiste en localStorage.
  const iso = (d) => d.toISOString().slice(0, 10);
  const today = new Date();
  const monthAgo = new Date(); monthAgo.setDate(today.getDate() - 30);
  const [dateFrom, setDateFrom] = useState(() => localStorage.getItem("cotflow_pipeline_from") || iso(monthAgo));
  const [dateTo, setDateTo] = useState(() => localStorage.getItem("cotflow_pipeline_to") || iso(today));
  const [datePreset, setDatePreset] = useState(() => localStorage.getItem("cotflow_pipeline_preset") || "30d");
  const navigate = useNavigate();

  const applyPreset = (preset) => {
    setDatePreset(preset);
    localStorage.setItem("cotflow_pipeline_preset", preset);
    if (preset === "all") { setDateFrom(""); setDateTo(""); return; }
    const t = new Date(); const from = new Date();
    if (preset === "7d") from.setDate(t.getDate() - 7);
    else if (preset === "30d") from.setDate(t.getDate() - 30);
    else if (preset === "90d") from.setDate(t.getDate() - 90);
    else if (preset === "ytd") { from.setMonth(0); from.setDate(1); }
    setDateFrom(iso(from)); setDateTo(iso(t));
  };
  useEffect(() => { if (dateFrom) localStorage.setItem("cotflow_pipeline_from", dateFrom); }, [dateFrom]);
  useEffect(() => { if (dateTo) localStorage.setItem("cotflow_pipeline_to", dateTo); }, [dateTo]);

  const canReassign = ["owner", "administrador", "supervisor"].includes(user?.role);

  const fetchData = useCallback(async () => {
    try {
      const calls = [api.get("/pipeline/stages"), api.get("/quotations")];
      if (["owner", "administrador", "supervisor"].includes(user?.role)) calls.push(api.get("/team/users"));
      const [stagesRes, qRes, teamRes] = await Promise.all(calls);
      setStages(stagesRes.data);
      setQuotations(qRes.data);
      if (teamRes?.data) setTeamUsers(teamRes.data);
    } catch { toast.error("Error al cargar datos"); }
    setLoading(false);
  }, [user?.role]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDragStart = (e, qid) => { setDragging(qid); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", qid); };
  const handleDragOver = (e, stageId) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOver(stageId); };
  const handleDragLeave = () => setDragOver(null);
  const handleDrop = (e, toStageId) => {
    e.preventDefault(); setDragOver(null);
    if (!dragging) return;
    const q = quotations.find(q => q.id === dragging);
    if (!q || q.status === toStageId) { setDragging(null); return; }
    setShowObsModal({ qid: dragging, fromStage: q.status, toStage: toStageId, qTitle: q.title });
    setDragging(null);
  };

  const confirmStatusChange = async () => {
    if (!showObsModal) return;
    try {
      const res = await api.put(`/quotations/${showObsModal.qid}/status`, { to_stage: showObsModal.toStage, observation });
      if (res.ok) { toast.success("Estado actualizado"); await fetchData(); }
      else toast.error("Error al cambiar estado");
    } catch { toast.error("Error al cambiar estado"); }
    setShowObsModal(null); setObservation("");
  };

  const loadHistory = async (q) => {
    setSelectedQ(q);
    try { const res = await api.get(`/quotations/${q.id}/history`); setHistory(res.data.history || []); }
    catch { setHistory([]); }
  };

  const openSettings = () => { setEditStages(JSON.parse(JSON.stringify(stages))); setShowSettings(true); };
  const saveStages = async () => {
    try {
      const res = await api.put("/pipeline/stages", editStages);
      if (res.ok) { toast.success("Pipeline actualizado"); setStages(editStages); setShowSettings(false); }
    } catch { toast.error("Error al guardar"); }
  };
  const addStage = () => {
    const nonFinal = editStages.filter(s => !s.is_final);
    const final = editStages.filter(s => s.is_final);
    setEditStages([...nonFinal, { id: `stage_${Date.now()}`, name: "Nueva Etapa", order: nonFinal.length, color: "#64748b", is_final: false }, ...final]);
  };
  const removeStage = (idx) => {
    const stage = editStages[idx];
    if (quotations.some(q => q.status === stage.id)) { toast.error("No puedes eliminar una etapa con cotizaciones"); return; }
    setEditStages(editStages.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i })));
  };
  const moveStage = (idx, dir) => {
    const ns = [...editStages]; const [rem] = ns.splice(idx, 1); ns.splice(idx + dir, 0, rem);
    setEditStages(ns.map((s, i) => ({ ...s, order: i })));
  };

  const assignableUsers = useMemo(() => {
    if (!reassign) return [];
    if (user?.role === "supervisor") {
      const own = teamUsers.find(u => u.id === user.id);
      const team = teamUsers.filter(u => u.supervisor_id === user.id);
      return [own, ...team].filter(Boolean);
    }
    return teamUsers.filter(u => u.is_active !== false);
  }, [reassign, teamUsers, user]);

  const confirmReassign = async (toUserId) => {
    if (!reassign) return;
    try {
      const res = await api.put(`/quotations/${reassign.id}/assign`, { to_user_id: toUserId });
      if (res.ok) { toast.success("Responsable actualizado"); await fetchData(); }
      else toast.error(res.data?.detail || "Error al reasignar");
    } catch { toast.error("Error al reasignar"); }
    setReassign(null);
  };

  const rolesEnPipeline = useMemo(() => {
    const set = new Set(quotations.map(q => q.owner_role).filter(Boolean));
    return Array.from(set);
  }, [quotations]);

  const filtered = useMemo(() => {
    // Set de ids de etapas marcadas como finales
    const finalIds = new Set(stages.filter(s => s.is_final).map(s => s.id));
    // Rango de fechas (inclusive). "to" incluye todo el día.
    const fromTs = dateFrom ? new Date(dateFrom + "T00:00:00").getTime() : null;
    const toTs = dateTo ? new Date(dateTo + "T23:59:59.999").getTime() : null;
    const dateOfStage = (q) => {
      // Cuándo entró la cotización a la etapa actual (última entrada del historial con to_stage = status).
      const hist = q.status_history || [];
      for (let i = hist.length - 1; i >= 0; i--) {
        if (hist[i]?.to_stage === q.status && hist[i]?.timestamp) {
          return new Date(hist[i].timestamp).getTime();
        }
      }
      return q.created_at ? new Date(q.created_at).getTime() : 0;
    };
    return quotations.filter(q => {
      if (roleFilter !== "all" && q.owner_role !== roleFilter) return false;
      if (ownerFilter !== "all" && q.owner_id !== ownerFilter) return false;
      // Filtro de fecha SOLO para etapas finales
      if (finalIds.has(q.status)) {
        const ts = dateOfStage(q);
        if (fromTs && ts < fromTs) return false;
        if (toTs && ts > toTs) return false;
      }
      return true;
    });
  }, [quotations, roleFilter, ownerFilter, stages, dateFrom, dateTo]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>;

  const stageMap = Object.fromEntries(stages.map(s => [s.id, s]));
  const qByStage = {};
  stages.forEach(s => { qByStage[s.id] = []; });
  filtered.forEach(q => {
    const sid = q.status || "creation";
    if (qByStage[sid]) qByStage[sid].push(q);
    else if (qByStage["creation"]) qByStage["creation"].push(q);
  });

  return (
    <div className="h-full flex flex-col" data-testid="pipeline-page">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Pipeline de Cotizaciones</h1>
          <p className="text-sm text-slate-500 mt-0.5">{filtered.length} de {quotations.length} cotización{quotations.length !== 1 ? "es" : ""}</p>
        </div>
        <button onClick={openSettings} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition" data-testid="pipeline-settings-btn">
          <Settings size={16} /> Configurar Etapas
        </button>
      </div>

      {/* Filtros por Rol, Creador y Fechas */}
      <div className="flex items-center gap-3 mb-4 flex-wrap bg-white border border-slate-200 rounded-lg px-4 py-2.5" data-testid="pipeline-filters">
        <Filter size={14} className="text-slate-400" />
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Filtrar</span>
        <div className="flex items-center gap-1.5">
          <label className="text-xs text-slate-500">Rol:</label>
          <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
            className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="filter-role">
            <option value="all">Todos</option>
            {rolesEnPipeline.map(r => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <label className="text-xs text-slate-500">Creador:</label>
          <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)}
            className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="filter-owner">
            <option value="all">Todos</option>
            {teamUsers.length > 0
              ? teamUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)
              : (() => {
                  const seen = new Map();
                  quotations.forEach(q => { if (q.owner_id && !seen.has(q.owner_id)) seen.set(q.owner_id, q.owner_name); });
                  return Array.from(seen.entries()).map(([id, name]) => <option key={id} value={id}>{name}</option>);
                })()}
          </select>
        </div>
        {(roleFilter !== "all" || ownerFilter !== "all") && (
          <button onClick={() => { setRoleFilter("all"); setOwnerFilter("all"); }} className="text-xs text-blue-600 hover:underline" data-testid="clear-filters">
            Limpiar filtros
          </button>
        )}
        <div className="h-6 w-px bg-slate-200 mx-1" />
        <Calendar size={14} className="text-slate-400" />
        <span className="text-xs text-slate-500 whitespace-nowrap">Cierres:</span>
        <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setDatePreset("custom"); }}
          className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="date-from" />
        <span className="text-xs text-slate-400">→</span>
        <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setDatePreset("custom"); }}
          className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="date-to" />
        <div className="flex items-center gap-1">
          {[["7d","7d"],["30d","30d"],["90d","90d"],["ytd","Año"],["all","Todo"]].map(([k, lbl]) => (
            <button key={k} onClick={() => applyPreset(k)}
              className={`text-[11px] px-2 py-1 rounded-md font-medium transition-all ${datePreset === k ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
              data-testid={`preset-${k}`}>{lbl}</button>
          ))}
        </div>
        <span className="text-[10px] text-slate-400 italic ml-auto">Solo etapas de cierre</span>
      </div>

      <div className="flex-1 overflow-x-auto -mx-2">
        <div className="flex gap-3 h-full min-w-max px-2 pb-4">
          {stages.map(stage => {
            const cards = qByStage[stage.id] || [];
            const totalValue = cards.reduce((s, q) => s + (q.total || 0), 0);
            return (
              <div key={stage.id}
                className={`w-72 flex-shrink-0 flex flex-col rounded-xl border transition-colors ${dragOver === stage.id ? "border-blue-400 bg-blue-50/60 shadow-lg shadow-blue-100/50" : "border-slate-200/70 bg-slate-100/50"}`}
                onDragOver={(e) => handleDragOver(e, stage.id)} onDragLeave={handleDragLeave} onDrop={(e) => handleDrop(e, stage.id)}
                data-testid={`pipeline-column-${stage.id}`}>
                <div className="px-3.5 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full ring-2 ring-white" style={{ backgroundColor: stage.color }} />
                    <span className="text-sm font-semibold text-slate-700">{stage.name}</span>
                    <span className="text-[10px] font-bold text-slate-400 bg-white px-1.5 py-0.5 rounded-full shadow-sm">{cards.length}</span>
                  </div>
                  {totalValue > 0 && <span className="text-[10px] font-medium text-slate-400">{fmtCurrency(totalValue, cards[0]?.currency || "CLP")}</span>}
                </div>
                <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-2" style={{ maxHeight: "calc(100vh - 300px)" }}>
                  {cards.map(q => (
                    <div key={q.id} draggable onDragStart={(e) => handleDragStart(e, q.id)} onClick={() => loadHistory(q)}
                      className={`bg-white rounded-lg border border-slate-200/80 p-3 cursor-grab active:cursor-grabbing shadow-sm hover:shadow-md hover:border-slate-300 transition-all ${dragging === q.id ? "opacity-30 scale-95" : ""}`}
                      data-testid={`pipeline-card-${q.id}`}>
                      <div className="font-medium text-sm text-slate-900 truncate">{q.title}</div>
                      <div className="text-xs text-slate-500 mt-1 truncate">{q.client_name}</div>
                      <div className="flex justify-between items-center mt-2.5">
                        <span className="text-sm font-bold" style={{ color: stage.color }}>{fmtCurrency(q.total || 0, q.currency)}</span>
                        <span className="text-[10px] text-slate-400">{timeAgo(q.created_at)}</span>
                      </div>
                      {/* Responsable */}
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                        <div className="flex items-center gap-1.5 min-w-0" data-testid={`owner-chip-${q.id}`}>
                          <UserIcon size={10} className="text-slate-400 flex-shrink-0" />
                          <span className="text-[10px] text-slate-600 truncate">{q.owner_name || "—"}</span>
                          {q.owner_role && (
                            <span className="text-[9px] text-slate-400 hidden sm:inline">· {ROLE_LABEL[q.owner_role] || q.owner_role}</span>
                          )}
                        </div>
                        {canReassign && (
                          <button onClick={(e) => { e.stopPropagation(); setReassign(q); }}
                            className="opacity-0 group-hover:opacity-100 hover:opacity-100 text-slate-400 hover:text-blue-600 p-0.5 transition"
                            title="Reasignar responsable" data-testid={`reassign-${q.id}`}>
                            <UserCog size={11} />
                          </button>
                        )}
                      </div>
                      {q.status_history?.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-slate-100 text-[10px] text-slate-400 truncate">
                          <MessageSquare size={9} className="inline mr-1" />{q.status_history[q.status_history.length - 1]?.observation || "Sin observación"}
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                        <span className="text-[10px] font-mono text-slate-400">V{q.version || 1}</span>
                        {q.status === "creation" && (
                          <button onClick={(e) => { e.stopPropagation(); navigate(`/app/quotations/edit/${q.id}`); }} className="text-[10px] text-amber-600 hover:text-amber-700 font-medium flex items-center gap-0.5 hover:underline" data-testid={`edit-pipeline-${q.id}`}>
                            <Pencil size={10} /> Editar
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {cards.length === 0 && <div className="text-center py-10 text-xs text-slate-400">Sin cotizaciones</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal Reasignar */}
      {reassign && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setReassign(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 mx-4" onClick={e => e.stopPropagation()} data-testid="reassign-modal">
            <h3 className="text-lg font-bold text-slate-900 mb-1 flex items-center gap-2"><UserCog size={18} /> Reasignar responsable</h3>
            <p className="text-sm text-slate-500 mb-4 truncate">{reassign.title}</p>
            <div className="text-xs text-slate-500 mb-2">
              Actual: <strong className="text-slate-700">{reassign.owner_name}</strong> {reassign.owner_role && <>· {ROLE_LABEL[reassign.owner_role] || reassign.owner_role}</>}
            </div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Nuevo responsable</label>
            <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
              {assignableUsers.length === 0 && <div className="p-4 text-sm text-slate-400 text-center">No hay usuarios disponibles</div>}
              {assignableUsers.map(u => (
                <button key={u.id} onClick={() => confirmReassign(u.id)}
                  disabled={u.id === reassign.owner_id}
                  className="w-full text-left px-3 py-2.5 hover:bg-slate-50 transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-between"
                  data-testid={`assign-to-${u.id}`}>
                  <div>
                    <div className="text-sm font-medium text-slate-900">{u.name}</div>
                    <div className="text-[10px] text-slate-500">{ROLE_LABEL[u.role] || u.role} · {u.email}</div>
                  </div>
                  {u.id === reassign.owner_id && <span className="text-[10px] text-blue-600 font-medium">Actual</span>}
                </button>
              ))}
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={() => setReassign(null)} className="flex-1 px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition">Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {showObsModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => { setShowObsModal(null); setObservation(""); }}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 mx-4" onClick={e => e.stopPropagation()} data-testid="status-change-modal">
            <h3 className="text-lg font-bold text-slate-900 mb-1">Cambiar Estado</h3>
            <p className="text-sm text-slate-500 mb-4 truncate">{showObsModal.qTitle}</p>
            <div className="flex items-center gap-3 mb-4 p-3 bg-slate-50 rounded-lg">
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: stageMap[showObsModal.fromStage]?.color || "#64748b" }}>{stageMap[showObsModal.fromStage]?.name || showObsModal.fromStage}</span>
              <ArrowRight size={16} className="text-slate-400 flex-shrink-0" />
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: stageMap[showObsModal.toStage]?.color || "#64748b" }}>{stageMap[showObsModal.toStage]?.name || showObsModal.toStage}</span>
            </div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Observación (opcional)</label>
            <textarea value={observation} onChange={e => setObservation(e.target.value)} placeholder="Ej: Cliente solicitó rebaja del 10%..." className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none" rows={3} data-testid="observation-input" autoFocus />
            <div className="flex gap-3 mt-4">
              <button onClick={() => { setShowObsModal(null); setObservation(""); }} className="flex-1 px-4 py-2 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition">Cancelar</button>
              <button onClick={confirmStatusChange} className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition" data-testid="confirm-status-change-btn">Confirmar</button>
            </div>
          </div>
        </div>
      )}

      {selectedQ && (
        <>
          <div className="fixed inset-0 bg-black/10 z-30" onClick={() => setSelectedQ(null)} />
          <div className="fixed inset-y-0 right-0 w-[380px] bg-white border-l border-slate-200 z-40 shadow-2xl overflow-y-auto" data-testid="history-sidebar">
            <div className="sticky top-0 bg-white/90 backdrop-blur-md border-b border-slate-200 px-5 py-4 flex items-center justify-between z-10">
              <h2 className="text-base font-bold text-slate-900 truncate pr-4">{selectedQ.title}</h2>
              <button onClick={() => setSelectedQ(null)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X size={18} /></button>
            </div>
            <div className="p-5">
              <div className="flex items-center gap-2 mb-5 px-3 py-2.5 rounded-lg" style={{ backgroundColor: `${stageMap[selectedQ.status]?.color || "#64748b"}12` }}>
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stageMap[selectedQ.status]?.color || "#64748b" }} />
                <span className="text-sm font-bold" style={{ color: stageMap[selectedQ.status]?.color || "#64748b" }}>{stageMap[selectedQ.status]?.name || selectedQ.status}</span>
              </div>
              <div className="grid grid-cols-2 gap-2.5 mb-6">
                {[
                  ["Cliente", selectedQ.client_name],
                  ["Total", fmtCurrency(selectedQ.total || 0, selectedQ.currency)],
                  ["Responsable", selectedQ.owner_name || "—"],
                  ["Rol", ROLE_LABEL[selectedQ.owner_role] || selectedQ.owner_role || "—"],
                  ["Creación", new Date(selectedQ.created_at).toLocaleDateString("es-CL")],
                  ["Moneda", selectedQ.currency],
                ].map(([label, value]) => (
                  <div key={label} className="bg-slate-50 rounded-lg p-2.5">
                    <div className="text-[10px] text-slate-400 uppercase tracking-wider">{label}</div>
                    <div className="text-xs font-semibold text-slate-700 truncate mt-0.5">{value}</div>
                  </div>
                ))}
              </div>
              <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5"><Clock size={11} /> Historial de Cambios</h3>
              {history.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-8 bg-slate-50 rounded-lg">Sin cambios de estado registrados</p>
              ) : (
                <div className="relative">
                  {history.slice().reverse().map((entry, i) => (
                    <div key={i} className="relative pl-6 pb-5 last:pb-0">
                      <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-slate-200 last:hidden" />
                      <div className="absolute left-[-3px] top-1.5 w-2 h-2 rounded-full border-2 border-white" style={{ backgroundColor: stageMap[entry.to_stage]?.color || "#3b82f6" }} />
                      <div className="text-[10px] text-slate-400 mb-0.5">{new Date(entry.timestamp).toLocaleString("es-CL")} · {entry.user_name}</div>
                      <div className="flex items-center gap-1.5 text-xs mb-1">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold text-white" style={{ backgroundColor: stageMap[entry.from_stage]?.color || "#64748b" }}>{entry.from_stage_name}</span>
                        <ChevronRight size={10} className="text-slate-300" />
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold text-white" style={{ backgroundColor: stageMap[entry.to_stage]?.color || "#64748b" }}>{entry.to_stage_name}</span>
                      </div>
                      {entry.observation && (
                        <div className="text-xs text-slate-600 bg-slate-50 rounded-lg p-2.5 mt-1 flex gap-1.5 items-start leading-relaxed">
                          <MessageSquare size={10} className="text-slate-400 mt-0.5 flex-shrink-0" />{entry.observation}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {showSettings && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setShowSettings(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6 mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="pipeline-settings-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900">Configurar Pipeline</h3>
              <button onClick={() => setShowSettings(false)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X size={18} /></button>
            </div>
            <p className="text-sm text-slate-500 mb-5">Define las etapas del flujo de tus cotizaciones. Puedes agregar etapas intermedias entre las predeterminadas.</p>
            <div className="space-y-2 mb-4">
              {editStages.map((stage, idx) => (
                <div key={stage.id} className="flex items-center gap-2 p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex flex-col gap-px">
                    <button disabled={idx === 0} onClick={() => moveStage(idx, -1)} className="text-slate-400 hover:text-slate-700 disabled:opacity-20 text-[10px] leading-none p-0.5">&#9650;</button>
                    <button disabled={idx === editStages.length - 1} onClick={() => moveStage(idx, 1)} className="text-slate-400 hover:text-slate-700 disabled:opacity-20 text-[10px] leading-none p-0.5">&#9660;</button>
                  </div>
                  <input type="color" value={stage.color} onChange={e => { const u = [...editStages]; u[idx] = { ...u[idx], color: e.target.value }; setEditStages(u); }} className="w-8 h-8 rounded cursor-pointer border-0 p-0" />
                  <input value={stage.name} onChange={e => { const u = [...editStages]; u[idx] = { ...u[idx], name: e.target.value }; setEditStages(u); }} className="flex-1 px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white" />
                  <label className="flex items-center gap-1 text-[10px] text-slate-500 whitespace-nowrap cursor-pointer select-none">
                    <input type="checkbox" checked={stage.is_final} onChange={e => { const u = [...editStages]; u[idx] = { ...u[idx], is_final: e.target.checked }; setEditStages(u); }} className="rounded w-3.5 h-3.5" /> Final
                  </label>
                  <button onClick={() => removeStage(idx)} className="text-slate-300 hover:text-red-500 p-1 transition"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
            <button onClick={addStage} className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium mb-5 hover:underline"><Plus size={14} /> Agregar Etapa Intermedia</button>
            <div className="flex gap-3">
              <button onClick={() => setShowSettings(false)} className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition">Cancelar</button>
              <button onClick={saveStages} className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition" data-testid="save-pipeline-settings-btn">Guardar Pipeline</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
